import { useLazyQuery, useMutation } from '@apollo/client';
import { useLingui } from '@lingui/react';
import { FunctionType } from '@zeus/index';
import { Form, Input, InputNumber, Select, DatePicker } from 'antd';
import { RangePickerProps } from 'antd/es/date-picker';
import dayjs, { Dayjs } from 'dayjs'; // dayjs,
import { useState, useEffect } from 'react';
import { GQL_CREATE_DEVIS_MAINTENANCE } from '~/gql/devis/devis';
import { StoredPoste } from '~/gql/poste/poste';
import { GQL_USERS, StoredUsers } from '~/gql/users/users';
import { authClient } from '~/helpers/apollo';

import DurationItem from '~/pages/components/ui/DurationItem';
import ModalCustom from '~/pages/components/ui/ModalCustom';

// copied from rc-picker/lib/interface.d.ts
declare type EventValue<DateType> = DateType | null;
declare type RangeValue<DateType> =
  | [EventValue<DateType>, EventValue<DateType>]
  | null;

type IProps = {
  projectId: number;
  devisId?: number;
  postes: StoredPoste[];
  title: string;
  isOpen: boolean;
  confirm: () => void;
  close: () => void;
};

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

function AddMaintenance({
  title,
  isOpen,
  confirm,
  close,
  projectId,
  devisId,
  postes,
}: IProps) {
  const { i18n } = useLingui();
  const { RangePicker } = DatePicker;

  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [userList, setUserList] = useState<StoredUsers[]>([]);

  const [taskName, setTaskName] = useState<string>('');

  const [amount, setAmount] = useState<number>(0);

  const [duration, setDuration] = useState<number>(0);
  const [posteId, setPosteId] = useState<number | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [startContrat, setStartContrat] = useState<Date | undefined>(undefined);
  const [endContrat, setEndContrat] = useState<Date | undefined>(undefined);

  const [createDevisMaintenance] = useMutation(GQL_CREATE_DEVIS_MAINTENANCE, {
    client: authClient,
  });

  const [getAllUsers] = useLazyQuery(GQL_USERS, {
    client: authClient,
    onCompleted: (data) => {
      setUserList(data.getUsers);
    },
  });

  useEffect(() => {
    form.resetFields();

    void getUserList();
    setAmount(0);

    setDuration(0);
    setTaskName('');
  }, []);

  async function getUserList() {
    setLoading(true);
    await getAllUsers();
    setLoading(false);
  }

  async function submit() {
    if (!taskName || !posteId || !startContrat || !endContrat) return;
    const createDevisMaintenaceInput = {
      projectId: projectId,
      devisId: devisId,
      startDate: startContrat.setHours(0, 0, 0),
      finishDate: endContrat.setHours(23, 59, 59),
      functionType: FunctionType.IT,
      amount: amount,
      duration: duration,
      taskName: taskName,
      posteId: posteId,
      users: selectedUsers,
    };
    const { data: _data } = await createDevisMaintenance({
      variables: {
        createDevisMaintenaceInput,
      },
    });
    if (_data?.createDevisMaintenanceAndTask) {
      //
      console.log(_data.createDevisMaintenanceAndTask);
      confirm();
    } else {
      close();
    }
    setDuration(0);
    form.resetFields();
  }

  const onChangeAmount = (value: number | null) => {
    if (value !== null) setAmount(value);
  };

  const onChangeDuration = (_duration: number) => {
    setDuration(_duration);
  };

  function onChangePoste(value: number) {
    setPosteId(value);
    const _poste = postes.find((el) => el.id === value);
    if (!form.getFieldValue('taskName')) {
      const name = _poste?.name ?? '';
      form.setFieldsValue({ taskName: name });
      setTaskName(name);
    }
  }

  const onRangeChange = (values: RangeValue<Dayjs>) => {
    if (values) {
      setStartContrat(values[0]?.toDate());
      setEndContrat(values[1]?.toDate());
    }
  };

  const disabledDate: RangePickerProps['disabledDate'] = (current: any) => {
    return current && current < dayjs().endOf('day');
  };

  return (
    <ModalCustom
      confirmDisabled={!taskName || !(duration || amount)}
      title={title}
      isOpen={isOpen}
      confirm={submit}
      close={() => {
        form.resetFields();
        close();
      }}
    >
      {loading ? (
        <p>{i18n._('common.loading')} ...</p>
      ) : (
        <>
          <Form
            form={form}
            {...layout}
            name="task-add"
            initialValues={{
              taskName: '',
              amount: 0,
              amountSpend: 0,
              duration: 0,
            }}
            style={{ width: 600 }}
          >
            <Form.Item
              name="poste"
              label={i18n._('common.maintenance.poste')}
              rules={[
                {
                  required: true,
                  message: i18n._('form.maintenance.placeholder'),
                },
              ]}
            >
              <Select
                showSearch
                style={{ width: '100%' }}
                value={posteId}
                onChange={(value) => onChangePoste(value)}
                optionFilterProp="label"
                options={postes.map((item) => ({
                  value: item.id,
                  label: i18n._(item.name),
                }))}
              />
            </Form.Item>
            <Form.Item
              name="taskName"
              label={i18n._('common.maintenance.name')}
              rules={[
                {
                  required: true,
                  message: i18n._('form.maintenance.placeholder'),
                },
              ]}
            >
              <Input
                placeholder={i18n._('form.maintenance.placeholder')}
                onChange={(e) => setTaskName(e.target.value)}
                value={taskName}
              />
            </Form.Item>

            <Form.Item
              name="range"
              label={i18n._('form.rangeContrat')}
              rules={[{ required: true, message: i18n._('errors.required') }]}
            >
              <RangePicker
                style={{ width: '100%' }}
                disabledDate={disabledDate}
                onChange={onRangeChange}
              />
            </Form.Item>

            <Form.Item
              name="duration"
              style={{ width: '100%' }}
              label={i18n._('common.duration')}
              rules={[
                {
                  required: true,
                  message: i18n._('form.duration.placeholder'),
                },
              ]}
            >
              <DurationItem
                $allowCorrection
                duration={duration}
                onChange={onChangeDuration}
                $align="flex-start"
              />
            </Form.Item>

            <Form.Item
              name="amount"
              style={{ width: '100%' }}
              label={i18n._('common.amount')}
              rules={[
                {
                  required: true,
                  message: i18n._('form.amount.placeholder'),
                },
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder={i18n._('form.amount.placeholder')}
                onChange={onChangeAmount}
                value={amount}
                controls={false}
                step="0.01"
                addonAfter="€"
              />
            </Form.Item>
            <Form.Item name="users" label={i18n._('form.edit.users')}>
              <Select
                mode="multiple"
                showSearch
                placeholder={i18n._('form.edit.users.placeholder')}
                value={selectedUsers}
                onChange={setSelectedUsers}
                style={{ width: '100%' }}
                optionFilterProp="label"
                options={userList.map((item) => ({
                  value: item.id,
                  label: item.firstName + ' ' + item.lastName,
                }))}
              />
            </Form.Item>
          </Form>
        </>
      )}
    </ModalCustom>
  );
}

export default AddMaintenance;
